/**
 * Quota Bar Component
 *
 * Renders a storage usage progress bar with human-readable sizes.
 * Color-coded: green (<70%), yellow (70-90%), red (>90%).
 */

import { formatBytes } from '../../../engine/services/storage-service.js';
import { t, formatNumber } from '../../../shared/i18n/index.js';
import { escapeHtml } from '../../../engine/core/validation.js';

const usageText = (used, quota, pct) => t('vault.quota.usage', { used: formatBytes(used), quota: formatBytes(quota), percent: formatNumber(Math.round(pct), { useGrouping: false }) });

/**
 * Create a quota bar element.
 * @param {number} used - Bytes used
 * @param {number} quota - Bytes allowed
 * @returns {HTMLElement}
 */
export function createQuotaBar(used, quota) {
  const pct = quota > 0 ? (used / quota) * 100 : used > 0 ? 100 : 0;
  const color = pct > 90 ? 'var(--accent-red, #ef4444)'
    : pct > 70 ? 'var(--accent-yellow, #f59e0b)'
    : 'var(--accent-green, #22c55e)';

  const el = document.createElement('div');
  el.className = 'quota-bar';
  el.innerHTML = `
    <div class="quota-bar-track">
      <div class="quota-bar-fill" style="width: ${Math.min(100, pct)}%; background: ${color}"></div>
    </div>
    <div class="quota-bar-label">${escapeHtml(usageText(used, quota, pct))}</div>
  `;
  return el;
}

/**
 * Update an existing quota bar element.
 * @param {HTMLElement} el - The quota bar element
 * @param {number} used
 * @param {number} quota
 */
export function updateQuotaBar(el, used, quota) {
  if (!el) return;
  const pct = quota > 0 ? (used / quota) * 100 : used > 0 ? 100 : 0;
  const color = pct > 90 ? 'var(--accent-red, #ef4444)'
    : pct > 70 ? 'var(--accent-yellow, #f59e0b)'
    : 'var(--accent-green, #22c55e)';

  const fill = el.querySelector('.quota-bar-fill');
  const label = el.querySelector('.quota-bar-label');
  if (fill) {
    fill.style.width = `${Math.min(100, pct)}%`;
    fill.style.background = color;
  }
  if (label) {
    label.textContent = usageText(used, quota, pct);
  }
}
